import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  open: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  headerActions?: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string; // Allow overriding container width/spacing
  /** Hide the default footer with the single “Fermer” button.
   *  Useful when the caller wants to render its own action buttons. */
  hideDefaultFooter?: boolean;
  /** Optional extra classes for the scrollable body container */
  bodyClassName?: string;
  /** Optional overlay classes (default: bg-black/30). Use e.g. bg-black/50 for a darker overlay. */
  overlayClassName?: string;  /** Hide the X close button in the header */
  hideCloseButton?: boolean;}

const Modal: React.FC<ModalProps> = ({
  open,
  title,
  description,
  headerActions,
  onClose,
  children,
  footer,
  className,
  hideDefaultFooter = false,
  bodyClassName,
  overlayClassName,
  hideCloseButton = false,
}) => {
  const { t } = useTranslation();
  if (!open) return null;
  const containerClasses =
    'relative rounded-xl border border-border/60 glass-card w-full mx-4 shadow-2xl animate-slide-up';
  const widthClass = className ? className : 'max-w-lg';
  // Rendered at the end of <body>: inside a page, the parent's spacing (e.g. space-y-*)
  // would add a margin to this fixed overlay and leave a strip uncovered at the top.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      {/* Glassmorphism backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${overlayClassName || ''}`}
        onClick={onClose}
      />
      <div className={`${containerClasses} ${widthClass}`.trim()}>
        <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {description ? (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerActions}
            {!hideCloseButton && (
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-muted/50">×</button>
            )}
          </div>
        </div>
        <div className={["p-4", bodyClassName].filter(Boolean).join(" ")}>
          {children}
        </div>
        {footer !== undefined ? (
          <div className="px-4 py-3 border-t border-border/60 flex justify-end gap-2">
            {footer}
          </div>
        ) : !hideDefaultFooter && (
          <div className="px-4 py-3 border-t border-border/60 flex justify-end">
            <Button onClick={onClose} variant="outline">
              {t('global.close')}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body

  );
};

export default Modal;

